import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const EUR = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const H = (n) => Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 });

const METRICS = [
  { key: "rev_m", label: "💶 Fatturato", fmt: (n) => (n ? EUR(n) : "—") },
  { key: "margin_m", label: "📊 Margine", fmt: (n) => (n ? EUR(n) : "—"), color: true },
  { key: "hours_m", label: "⏱ Ore", fmt: (n) => (n ? H(n) : "—") },
  { key: "docs_m", label: "🧾 Registrazioni", fmt: (n) => n || "—" },
];

const PERIODS = ["mensile", "bimestrale", "trimestrale", "semestrale", "annuale", "cessato"];

export default function GestioneTable({ onData }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("rev_m");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: 1 });

  const setSortKey = (key) => setSort((s) => s.key === key ? { key, dir: -s.dir } : { key, dir: key === "name" ? 1 : -1 });
  const arrow = (key) => sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : "";

  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const load = (silent) => {
    if (!silent) setData(null);
    api.get(`/invoices/gestione?year=${year}`).then((r) => {
      setData(r.data);
      if (onData && year === new Date().getFullYear()) onData(r.data);
    }).catch(() => {});
  };

  const savePeriod = async (cid, val) => {
    try {
      await api.put("/invoices/billing-period", { company_id: cid, period: val || null });
      load(true);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const M = METRICS.find((m) => m.key === metric);
  const cell = (v, key) => (
    <td key={key} style={{ textAlign: "right", whiteSpace: "nowrap", color: M.color && v ? (v >= 0 ? "var(--green)" : "var(--danger)") : undefined }}>{M.fmt(v)}</td>
  );

  const rows = data ? data.clients
    .filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => sort.key === "name"
      ? sort.dir * a.name.localeCompare(b.name, "it", { sensitivity: "base" })
      : sort.dir * ((a[sort.key] || 0) - (b[sort.key] || 0))) : [];
  const filtered = q.trim() !== "";
  const t = data?.totals;
  const ft = filtered ? {
    m: MONTHS.map((_, i) => rows.reduce((s, c) => s + (Number(c[metric][i]) || 0), 0)),
    revenue_ytd: rows.reduce((s, c) => s + c.revenue_ytd, 0),
    docs_ytd: rows.reduce((s, c) => s + c.docs_ytd, 0),
    hours_ytd: rows.reduce((s, c) => s + c.hours_ytd, 0),
    margin_ytd: rows.reduce((s, c) => s + c.margin_ytd, 0),
    projected: rows.reduce((s, c) => s + (c.projected || 0), 0),
  } : null;

  return (
    <div className="panel" data-testid="gestione-panel">
      <div className="toolbar" style={{ gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>🎛 Controllo di gestione</h3>
        <button className="btn sm" onClick={() => setYear((y) => y - 1)} data-testid="gest-year-prev">◀</button>
        <b data-testid="gest-year-label">{year}</b>
        <button className="btn sm" onClick={() => setYear((y) => y + 1)} data-testid="gest-year-next">▶</button>
        <span style={{ display: "inline-flex", gap: 4 }}>
          {METRICS.map((m) => (
            <button key={m.key} className={"btn sm" + (metric === m.key ? " primary" : "")} onClick={() => setMetric(m.key)} data-testid={`gest-metric-${m.key}`}>{m.label}</button>
          ))}
        </span>
        <input placeholder="Cerca cliente…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 200 }} data-testid="gest-search" />
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Mese per mese: <b>{M.label.replace(/^\S+ /, "")}</b> per cliente (imponibile, senza IVA). Le ore includono registrazioni, email, WhatsApp, incarichi e appuntamenti.
        La <b>proiezione</b> riguarda solo i clienti ricorrenti (fatturato sia nel {year - 1} sia nel {year}); le quote annuali non vengono annualizzate. Giroconti spalmati sui mesi trascorsi.
      </p>
      {!data ? <p className="muted">Calcolo in corso…</p> : (
        <div className="dc-wrap" style={{ maxHeight: "75vh" }}>
          <table style={{ fontSize: 12.5 }} className="dc-sticky" data-testid="gestione-table">
            <thead>
              <tr>
                <th style={{ minWidth: 170, cursor: "pointer" }} onClick={() => setSortKey("name")} data-testid="gest-sort-name" title="Ordina per nome">Cliente{arrow("name")}</th>
                {MONTHS.map((m, i) => <th key={m} style={{ textAlign: "right", background: i + 1 === new Date().getMonth() + 1 && year === new Date().getFullYear() ? "#27407a" : undefined }}>{m}</th>)}
                <th style={{ textAlign: "right", borderLeft: "2px solid var(--border)", cursor: "pointer" }} onClick={() => setSortKey("revenue_ytd")} data-testid="gest-sort-revenue" title="Ordina per fatturato">Fatturato ad oggi{arrow("revenue_ytd")}</th>
                <th style={{ textAlign: "right", cursor: "pointer" }} onClick={() => setSortKey("docs_ytd")} data-testid="gest-sort-docs" title="Ordina per registrazioni">Reg.{arrow("docs_ytd")}</th>
                <th style={{ textAlign: "right", cursor: "pointer" }} onClick={() => setSortKey("hours_ytd")} data-testid="gest-sort-hours" title="Ordina per ore">Ore{arrow("hours_ytd")}</th>
                <th style={{ textAlign: "right", cursor: "pointer" }} onClick={() => setSortKey("margin_ytd")} data-testid="gest-sort-margin" title="Ordina per margine">Margine ad oggi{arrow("margin_ytd")}</th>
                <th>Periodicità</th>
                <th style={{ textAlign: "right" }}>Proiezione {year} (ricorrenti)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.company_id} data-testid="gestione-row">
                  <td style={{ fontWeight: 600 }}><Link to={`/aziende/${c.company_id}`}>{c.name}</Link></td>
                  {MONTHS.map((_, i) => cell(c[metric][i], i))}
                  <td style={{ textAlign: "right", fontWeight: 700, borderLeft: "2px solid var(--border)" }}>{EUR(c.revenue_ytd)}</td>
                  <td style={{ textAlign: "right" }}>{c.docs_ytd || "—"}</td>
                  <td style={{ textAlign: "right" }}>{H(c.hours_ytd)} h</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: c.margin_ytd >= 0 ? "var(--green)" : "var(--danger)" }}>{EUR(c.margin_ytd)}</td>
                  <td style={{ whiteSpace: "nowrap", background: c.period_uncertain ? "rgba(250, 204, 21, .10)" : undefined }} title={c.period_uncertain ? "Periodicità incerta: verificala e correggila se serve" : (c.period_override ? "Impostata a mano" : "Rilevata dalla cadenza delle fatture")}>
                    <select value={c.period_override || ""} onChange={(e) => savePeriod(c.company_id, e.target.value)}
                      style={{ fontSize: 11, padding: "2px 4px", maxWidth: 118 }} data-testid={`gest-period-${c.company_id}`}>
                      <option value="">{c.period_detected ? `auto: ${c.period_detected}` : "auto: —"}</option>
                      {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {c.period_uncertain && " ⚠️"}
                  </td>
                  <td style={{ textAlign: "right" }} className={c.recurring ? "" : "muted"}>{c.projected != null ? EUR(c.projected) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700 }} data-testid="gestione-totals">
                <td>Totale {filtered ? `(${rows.length} filtrati)` : `(${rows.length} clienti)`}</td>
                {MONTHS.map((_, i) => {
                  const v = filtered ? ft.m[i] : t[metric][i];
                  return <td key={i} style={{ textAlign: "right", whiteSpace: "nowrap", color: M.color && v ? (v >= 0 ? "var(--green)" : "var(--danger)") : undefined }}>{M.fmt(v)}</td>;
                })}
                <td style={{ textAlign: "right", borderLeft: "2px solid var(--border)" }}>{EUR(filtered ? ft.revenue_ytd : t.revenue_ytd)}</td>
                <td style={{ textAlign: "right" }}>{(filtered ? ft.docs_ytd : t.docs_ytd) || "—"}</td>
                <td style={{ textAlign: "right" }}>{H(filtered ? ft.hours_ytd : t.hours_ytd)} h</td>
                <td style={{ textAlign: "right", color: (filtered ? ft.margin_ytd : t.margin_ytd) >= 0 ? "var(--green)" : "var(--danger)" }}>{EUR(filtered ? ft.margin_ytd : t.margin_ytd)}</td>
                <td />
                <td style={{ textAlign: "right" }}>{EUR(filtered ? ft.projected : t.projected_recurring)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
